import { StaffContactFields } from 'src/components/AccountAccess/StaffContactFields';
import { useStaffLoginContacts } from 'src/components/AccountAccess/useStaffLoginContacts';
import { useMemo, useState } from 'react';
import cx from 'classnames';
import { mutate } from 'swr';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Section } from 'src/components/Section';
import { TextIcon } from 'src/components/Icons/TextIcon';
import { DocumentsIcon, PlusIcon } from 'src/components/Icons/icons';
import { NextOfKin, NextOfKinRelationType } from 'src/domain/next-of-kin';
import { Consumer } from 'src/domain/consumer';
import { OrganizationRolePermission } from 'src/domain/organization';
import { getFullName, isOrganizationAdministrator } from 'src/domain/user';
import { useNextOfKins } from 'src/view/hooks/nextOfKin';
import { useSignInIdentities } from 'src/view/hooks/useSignInIdentities';
import { Table } from 'src/components/Table';
import { formatPhoneNumber } from 'src/utils/stringUtils';
import { useAvatar } from 'src/view/hooks/useAvatar';
import { useCreatorRolePermission } from 'src/view/hooks/useCreatorRolePermission';
import container from 'src/container';
import { useNotification } from 'src/components/Notification/useNotfication';
import { ScrollableModal } from 'src/components/Modals/ScrollableModal';
import Form from 'src/components/FormComponents/Form';
import Input, {
    Error as InputError,
} from 'src/components/FormComponents/Input';
import CheckBox from 'src/components/FormComponents/CheckBox';
import {
    NextOfKinEditSchema,
    NextOfKinEditValues,
} from 'src/view/screens/NextOfKin/nextOfKin.schema';
import { Button } from 'src/components/Buttons/Button';
import { badgeVariantToClassName } from 'src/utils/badgeUtils';
import { useSignedInUser } from 'src/view/hooks/useSignedInUser';
import { LoginStatusIndicator } from 'src/components/LoginStatusIndicator';
import avatarDefault from 'src/assets/images/client_default.png';

const {
    cradle: { munduClient },
} = container;

type NextOfKinRowProps = {
    item: NextOfKin;
    consumerId: Consumer['id'];
    onDelete: (id: NextOfKin['id']) => void;
    canEdit?: boolean;
};

const NextOfKinTableRow = ({
    item,
    consumerId,
    onDelete,
    canEdit,
}: NextOfKinRowProps) => {
    const { email } = item;
    const { phoneNumber: loginPhone } = useSignInIdentities(item.id);
    // Unconfirmed phones added by staff are not login identities yet.
    const phoneNumber = loginPhone ?? item.phoneNumber;
    const { src: avatarSrc } = useAvatar(item.profile.avatar, {
        firstName: item.profile.firstName,
        lastName: item.profile.lastName,
        sasConsumerId: item.id,
    });
    const { t } = useTranslation();
    const notify = useNotification();
    const user = useSignedInUser();
    const isOrganization = isOrganizationAdministrator(user);
    const [isEditing, setIsEditing] = useState(false);
    const [confirmDeleteValue, setConfirmDeleteValue] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);
    // Loaded with the list, so the edit dialog opens and closes in its final state.
    const contacts = useStaffLoginContacts(
        'nextOfKins',
        item.id,
        item.organizationId,
        Boolean(canEdit),
        {
            email: item.email,
            phone: item.phoneNumber,
            pending: item.hasEverLoggedIn === false,
        },
    );
    const closeEditor = () => {
        contacts.reset();
        setIsEditing(false);
    };

    const relationLabel = useMemo(() => {
        if (item.relation === NextOfKinRelationType.external)
            return t('nextOfKin.form.relationGuardian');
        return t('nextOfKin.form.relationFamily');
    }, [item.relation, t]);

    const handleEdit = async ({
        firstName,
        lastName,
        relation,
        isMainContact,
        isGuardian,
        disableManagingRelatives,
    }: NextOfKinEditValues) => {
        try {
            // Contacts are saved first; a refused contact keeps the form open with its error.
            if (!(await contacts.save())) return;
            await munduClient.updateNextOfKin(item.id, {
                profile: {
                    firstName,
                    lastName,
                },
                relation,
                isMainContact,
                isGuardian,
                disableManagingRelatives: isMainContact
                    ? disableManagingRelatives ?? false
                    : false,
            });
            await mutate([consumerId, 'nextOfKin/consumerId']);
            notify(t('nextOfKin.notifications.updated'));
            closeEditor();
        } catch (e) {
            if (e instanceof Error) notify(e.message, true);
        }
    };

    const formKey = `${item.id}-${item.profile.firstName}-${item.profile.lastName}`;

    const deleteConfirmationTarget =
        item.profile.lastName ?? item.profile.firstName;
    const canDelete =
        confirmDeleteValue.trim().toLowerCase() ===
        deleteConfirmationTarget.toLowerCase();

    const handleDelete = async () => {
        if (!canDelete || isDeleting) return;
        try {
            setIsDeleting(true);
            await Promise.resolve(onDelete(item.id));
            setIsEditing(false);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <>
            <tr className="text-woodyBrown align-top">
                <td className="px-4 py-3">
                    <div
                        className="group flex cursor-pointer items-center gap-3"
                        role="button"
                        tabIndex={0}
                        onClick={() => setIsEditing(true)}
                        onKeyDown={event => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                setIsEditing(true);
                            }
                        }}
                    >
                        <span className="relative flex h-10 w-10 flex-shrink-0">
                            <img
                                className="h-10 w-10 rounded-full object-cover"
                                alt={getFullName(item)}
                                src={avatarSrc}
                                onError={event => {
                                    event.currentTarget.src = avatarDefault;
                                }}
                            />
                            <LoginStatusIndicator
                                className="absolute bottom-0 right-0 ring-2 ring-vanillaWhite"
                                hasEverLoggedIn={item.hasEverLoggedIn}
                                lastLoggedInAt={item.lastLoggedInAt}
                                size="avatar"
                            />
                        </span>
                        <div className="space-y-1">
                            <div className="font-semibold group-hover:underline">
                                {getFullName(item)}
                            </div>
                            <div className="flex flex-wrap items-center gap-1.5">
                                {item.isMainContact ? (
                                    <span
                                        className={cx(
                                            'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold',
                                            badgeVariantToClassName['main'],
                                        )}
                                    >
                                        {t(
                                            'modules.messages.form.recipientsBadgeMainContact',
                                        )}
                                    </span>
                                ) : null}
                                {item.isGuardian ? (
                                    <span
                                        className={cx(
                                            'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold',
                                            badgeVariantToClassName['guardian'],
                                        )}
                                    >
                                        {t(
                                            'modules.messages.form.recipientsBadgeGuardian',
                                        )}
                                    </span>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </td>
                <td className="px-4 py-3 content-center text-sm text-mongooseGray">
                    {relationLabel}
                </td>
                <td className="px-4 py-3 content-center text-sm text-mongooseGray">
                    {phoneNumber ? formatPhoneNumber(phoneNumber) : '-'}
                </td>
                <td className="px-4 py-3 content-center text-sm text-mongooseGray break-words">
                    {email ?? '-'}
                </td>
                {canEdit && (
                    <td className="px-4 py-3 content-center text-right">
                        <button
                            type="button"
                            className="primary-btn-small p-1.5"
                            onClick={() => setIsEditing(true)}
                        >
                            <div className="relative w-4 h-4">
                                <DocumentsIcon />
                            </div>
                        </button>
                    </td>
                )}
            </tr>
            <ScrollableModal visible={isEditing} onClose={closeEditor}>
                <div className="text-woodyBrown flex flex-col gap-6 w-[min(32rem,calc(100vw-4rem))]">
                    <div>
                        <h3 className="text-2xl font-semibold">
                            {t('nextOfKin.card.editTitle')}
                        </h3>
                        <p className="text-mongooseGray mt-1">
                            {t('nextOfKin.card.editDescription', {
                                name: getFullName(item),
                            })}
                        </p>
                    </div>
                    <Form
                        key={formKey}
                        schema={NextOfKinEditSchema}
                        onSubmit={handleEdit}
                        mode="onChange"
                        shouldUnregister
                        defaultValues={{
                            firstName: item.profile.firstName,
                            lastName: item.profile.lastName,
                            relation: item.relation,
                            isMainContact: item.isMainContact ?? false,
                            isGuardian: item.isGuardian ?? false,
                            disableManagingRelatives:
                                item.disableManagingRelatives ?? false,
                        }}
                    >
                        {({
                            register,
                            watch,
                            formState: { errors, isSubmitting },
                        }) => (
                            <div className="flex flex-col gap-4">
                                <Input
                                    className="text-input"
                                    type="text"
                                    label={t('nextOfKin.form.firstName')}
                                    error={errors.firstName}
                                    disabled={!canEdit}
                                    {...register('firstName')}
                                />
                                <Input
                                    className="text-input"
                                    type="text"
                                    label={t('nextOfKin.form.lastName')}
                                    error={errors.lastName}
                                    disabled={!canEdit}
                                    {...register('lastName')}
                                />
                                {canEdit && (
                                    <StaffContactFields
                                        contacts={contacts}
                                        disabled={isSubmitting}
                                        labels={{
                                            email: t('nextOfKin.form.email'),
                                            phone: t('nextOfKin.form.phone'),
                                        }}
                                    />
                                )}
                                <div>
                                    <label className="mb-1 block text-sm font-medium text-woodyBrown">
                                        {t('nextOfKin.form.relationLabel')}
                                    </label>
                                    <select
                                        className="text-input w-full"
                                        aria-invalid={Boolean(errors.relation)}
                                        disabled={!canEdit}
                                        {...register('relation')}
                                    >
                                        <option value="">
                                            {t('nextOfKin.form.selectRelation')}
                                        </option>
                                        <option
                                            value={NextOfKinRelationType.family}
                                        >
                                            {t('nextOfKin.form.relationFamily')}
                                        </option>
                                        <option
                                            value={
                                                NextOfKinRelationType.external
                                            }
                                        >
                                            {t(
                                                'nextOfKin.form.relationGuardian',
                                            )}
                                        </option>
                                    </select>
                                    <InputError error={errors.relation} />
                                </div>
                                <CheckBox
                                    label={t(
                                        'modules.messages.form.recipientsBadgeMainContact',
                                    )}
                                    disabled={!canEdit}
                                    {...register('isMainContact')}
                                />
                                <CheckBox
                                    label={t('nextOfKin.form.isGuardianLabel')}
                                    disabled={!canEdit}
                                    {...register('isGuardian')}
                                />

                                {watch('isMainContact') && isOrganization && (
                                    <CheckBox
                                        label={t(
                                            'nextOfKin.form.disableManagingRelativesLabel',
                                        )}
                                        {...register(
                                            'disableManagingRelatives',
                                        )}
                                    />
                                )}
                                {canEdit && (
                                    <div className="flex justify-end gap-3 pt-2">
                                        <button
                                            type="button"
                                            className="px-4 py-2 text-sm text-woodyBrown hover:underline"
                                            onClick={closeEditor}
                                        >
                                            {t('common.cancel')}
                                        </button>
                                        <Input
                                            value={t('common.save')}
                                            className="primary-btn-small"
                                            disabled={isSubmitting}
                                            type="submit"
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </Form>

                    {canEdit && (
                        <div className="border-t border-woodyBrown/20 pt-6">
                            <h4 className="text-2xl font-semibold">
                                {t('common.delete')}
                            </h4>
                            <p className="text-mongooseGray mt-1">
                                {t('nextOfKin.card.deleteConfirm')}
                            </p>
                            <div className="mt-4 flex items-end gap-3 flex-wrap">
                                <Input
                                    className="text-input"
                                    type="text"
                                    label={t('common.confirmDelete', {
                                        value: deleteConfirmationTarget,
                                    })}
                                    onChange={e =>
                                        setConfirmDeleteValue(
                                            e.currentTarget.value,
                                        )
                                    }
                                />
                                <Button
                                    title={t('common.delete')}
                                    onClick={handleDelete}
                                    disabled={!canDelete || isDeleting}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </ScrollableModal>
        </>
    );
};

export const NextOfKinList = ({
    consumerId,
    onDelete,
}: {
    consumerId: Consumer['id'];
    onDelete: (id: NextOfKin['id']) => void;
}) => {
    const { t } = useTranslation();
    const history = useHistory();
    const user = useSignedInUser();
    const { data: nextOfKins } = useNextOfKins({ consumerId });
    const hasEditPermission = useCreatorRolePermission(
        OrganizationRolePermission.writeResidents,
    );
    const canEdit = !user?.supportMode && hasEditPermission;

    const columns = useMemo(
        () => [
            { header: t('common.name') },
            { header: t('nextOfKin.form.relationLabel') },
            { header: t('common.phone') },
            { header: t('common.email') },
            ...(canEdit ? [{ header: '', className: 'w-0 text-right' }] : []),
        ],
        [t, canEdit],
    );

    const isLoading = !nextOfKins;

    const handleCreate = () => {
        history.push('/app/nextofkins/new');
    };

    return (
        <div className="h-full relative text-woodyBrown">
            <Section
                title={t('nextOfKin.list.title')}
                action={
                    canEdit ? (
                        <button
                            type="button"
                            className="primary-btn-small flex items-center gap-1 pl-2 pr-4 py-1"
                            onClick={handleCreate}
                        >
                            <TextIcon SVG={PlusIcon} />
                            <span>{t('nextOfKin.actions.new')}</span>
                        </button>
                    ) : undefined
                }
            >
                <Table
                    columns={columns}
                    data={nextOfKins ?? []}
                    renderRow={item => (
                        <NextOfKinTableRow
                            key={item.id}
                            item={item}
                            consumerId={consumerId}
                            onDelete={onDelete}
                            canEdit={canEdit}
                        />
                    )}
                    emptyMessage={t('nextOfKin.list.empty')}
                    isLoading={isLoading}
                    loadingRowCount={2}
                />
            </Section>
        </div>
    );
};
